import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Camera, 
  Mic, 
  Monitor, 
  Film,
  Headphones,
  Zap,
  Eye
} from 'lucide-react';
import Card from '../../components/UI/Card';
import ContactPreview from '../../components/Home/ContactPreview';

const AVProduction: React.FC = () => {
  const services = [
    {
      icon: Camera,
      title: "Professional Video Production",
      description: "High-quality video production for major events including IFFI documentation, Global Maritime India Summit coverage, and government policy launch recordings with cutting-edge 4K equipment."
    },
    {
      icon: Mic,
      title: "Audio Recording & Mixing",
      description: "Professional audio services for large-scale events like Global Ayurveda Conference at Thiruvananthapuram, trade fair announcements, and multilingual event coverage."
    },
    {
      icon: Monitor,
      title: "Live Streaming Solutions",
      description: "Multi-platform live streaming for government launches (PM Vishwakarma Yojana, Bhima Sakhi Yojana), international conferences, and cultural festival broadcasts with HD quality."
    },
    {
      icon: Film,
      title: "Post-Production Services",
      description: "Complete post-production for festival documentaries, government campaign videos, tourism promotion content, and corporate event highlights with professional editing suites."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Audio Visual Production - Mirchi Republic Media Production</title>
        <meta name="description" content="Professional audio visual production services including video production, live streaming, audio recording, and post-production for events and corporate content." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-purple-600 to-blue-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 800 600" className="w-full h-full" fill="currentColor">
              <defs>
                <pattern id="av-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                  <circle cx="40" cy="40" r="2" />
                  <circle cx="20" cy="20" r="1" />
                  <circle cx="60" cy="60" r="1" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="800" height="600" fill="url(#av-pattern)" />
            </svg>
          </div>
          
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1 
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Audio Visual Production
              </motion.h1>
              <motion.p 
                className="text-xl lg:text-2xl opacity-90 max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Bringing Your Vision to Life with Professional Audio Visual Solutions
              </motion.p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-8">Professional AV Solutions</h2>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  We provide comprehensive audio visual production services including video recording, live streaming, 
                  and post-production for major events. Our portfolio includes coverage of prestigious events like 
                  the International Film Festival of India, Global Maritime India Summit, and government conferences.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-6">Our AV Production Services</h2>
                <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
                  Complete audio visual production solutions from concept to final delivery
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="p-8 h-full hover:shadow-lg transition-shadow duration-300">
                      <service.icon className="w-16 h-16 text-purple-600 mb-6" />
                      <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                      <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        {service.description}
                      </p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technical Excellence Section */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-8">Technical Excellence</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <Card className="p-6 text-center">
                    <Eye className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">4K Quality</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Ultra-high definition video production with professional cinema-grade equipment
                    </p>
                  </Card>
                  <Card className="p-6 text-center">
                    <Headphones className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Studio-Grade Audio</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Crystal-clear audio recording and mixing with professional studio equipment
                    </p>
                  </Card>
                  <Card className="p-6 text-center">
                    <Zap className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Real-Time Production</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Live production capabilities with instant switching and streaming solutions
                    </p>
                  </Card>
                </div>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Our commitment to technical excellence ensures that every audio visual production meets 
                  international standards and exceeds client expectations through innovative solutions and meticulous attention to detail.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <ContactPreview />
      </div>
    </>
  );
};

export default AVProduction;